import LegalLayout, { P, H2, UL } from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="February 2026">
      <P>This Privacy Policy explains how Vendorly ("we", "our", grahakly.app) collects, uses, stores, and protects personal information from two groups: <strong>vendors</strong> (business owners who register accounts) and <strong>end customers</strong> (people who scan a vendor's QR code and share their contact details).</P>

      <H2>1. Information we collect</H2>
      <P><strong>From vendors:</strong> name, email address, phone number, business name, business type, hashed password, IP address, subscription and payment status.</P>
      <P><strong>From end customers:</strong> name, mobile number, and optionally email address and preferences — only the details a customer voluntarily enters into a vendor's QR form.</P>

      <H2>2. How we use it</H2>
      <UL>
        <li>To operate vendor accounts, dashboards, QR codes, and WhatsApp campaigns.</li>
        <li>To send transactional emails (verification, password reset, payment receipts) via Resend.</li>
        <li>To deliver WhatsApp messages to end customers via Twilio's Meta-approved infrastructure.</li>
        <li>To process subscription payments via Razorpay.</li>
        <li>To prevent fraud, abuse, and spam through IP and phone-based rate limits.</li>
      </UL>

      <H2>3. Legal basis (India DPDP Act, 2023)</H2>
      <P>We process personal data on the following lawful bases: (a) consent — customers voluntarily submit their number to a vendor's QR form; (b) contract — to deliver the paid service to vendors; (c) legitimate interest — to secure the platform against abuse; (d) legal obligation — to retain payment records for statutory audits.</P>

      <H2>4. Data sharing</H2>
      <P>We share personal data only with the following data processors, strictly for the purposes listed:</P>
      <UL>
        <li><strong>Twilio</strong> (USA) — to deliver WhatsApp messages using Meta-approved templates.</li>
        <li><strong>Razorpay</strong> (India) — to process subscription payments.</li>
        <li><strong>Resend</strong> (USA) — to send transactional emails.</li>
        <li><strong>MongoDB Atlas</strong> (Mumbai region preferred) — to store account and campaign data.</li>
      </UL>
      <P>We never sell, rent, or exchange customer phone numbers with any third party. Each vendor's customer list is isolated at the database level and cannot be accessed by other vendors.</P>

      <H2>5. Customer opt-out (STOP)</H2>
      <P>Every WhatsApp message we send contains a "Reply STOP to opt out" line. When a customer replies STOP, we mark that phone number as opted-out for that vendor within 24 hours and no further messages are sent.</P>

      <H2>6. Data retention</H2>
      <UL>
        <li>Active vendor and customer records: retained while the vendor's account is active.</li>
        <li>Deleted accounts: all personal data is purged within 30 days of deletion request.</li>
        <li>Payment records: retained for 7 years to meet Indian tax and audit obligations.</li>
        <li>Server logs: 90 days rolling window.</li>
      </UL>

      <H2>7. Security</H2>
      <UL>
        <li>All traffic is encrypted in transit with HTTPS/TLS 1.2+.</li>
        <li>Passwords are hashed with bcrypt (12 rounds) — we never store plain-text passwords.</li>
        <li>JSON Web Tokens are transmitted as HttpOnly cookies to defend against XSS.</li>
        <li>API responses expose customer phone numbers only in masked form (********1234).</li>
        <li>Cross-vendor data access is blocked at every database query.</li>
      </UL>

      <H2>8. Your rights</H2>
      <P>You have the right to access, correct, export, or delete your personal data. To exercise any of these rights, email <a href="mailto:vendorlyadmin@gmail.com" className="text-brand-primary font-semibold">vendorlyadmin@gmail.com</a> from the email address linked to your account. We will respond within 30 days.</P>

      <H2>9. Cookies</H2>
      <P>We use only two cookies: an <em>access</em> cookie (30 minutes) and a <em>refresh</em> cookie (7 days), both strictly necessary to keep vendors signed in. We do not use advertising or third-party tracking cookies. We do use PostHog for anonymous product analytics with cross-origin session replay — this can be disabled by using a privacy-focused browser or extension.</P>

      <H2>10. Children</H2>
      <P>Vendorly is not directed at children under 18. If we learn we have collected personal data from a minor, we delete it promptly.</P>

      <H2>11. Grievance officer</H2>
      <P>Under India's DPDP Act, 2023, our grievance officer is:</P>
      <UL>
        <li><strong>Name:</strong> Dikshant Agarwal</li>
        <li><strong>Email:</strong> vendorlyadmin@gmail.com</li>
        <li><strong>Response time:</strong> within 15 days</li>
      </UL>

      <H2>12. Changes to this policy</H2>
      <P>We may update this policy over time. Material changes will be announced via email to registered vendors at least 7 days before taking effect.</P>
    </LegalLayout>
  );
}
